import styles from "./AssetAccordion.module.css";
import {
  ProjectAssetType,
  selectedNodeTypeAtom,
  selectedAssetIdAtom,
  SelectableNodeTypes,
  isViewOnlyModeAtom,
  ZapMetadata,
} from "@zapier/canvas-state";
import { MouseEvent, KeyboardEvent, useCallback, useState } from "react";
import { Icon, Link, Text, TooltipWrapper } from "@zapier/design-system";
import classNames from "classnames";
import { useAtom, useSetAtom, useAtomValue } from "jotai";

import { useEmitUserInteractionEvents } from "../../hooks/useEmitUserInteractionEvents";
import { buildEditorLink } from "../../utils/buildEditorLink";

type Props = {
  assetType: ProjectAssetType;
  items: ZapMetadata[];
};

function getIconName(assetType: ProjectAssetType) {
  switch (assetType) {
    case "zap":
      return "miscBoltAltFill";
    case "interface":
      return "navInterfaces";
    case "table":
      return "navTables";
    case "chatbot":
      return "navAIChatbot";
    default:
      return "miscBoltAltFill";
  }
}

function getAssetUrl(assetType: ProjectAssetType, item: ZapMetadata) {
  const id = item.zap_id;

  switch (assetType) {
    case "zap": {
      const isPublished = item.is_published;
      return buildEditorLink(id, isPublished);
    }
  }
}

function convertToSelectableNodeType(assetType: ProjectAssetType) {
  switch (assetType) {
    case "chatbot":
      return "chatbots";
  }

  return assetType as SelectableNodeTypes;
}

export default function AssetAccordion({ assetType, items }: Props) {
  const { emitEventOnAssetOpen } = useEmitUserInteractionEvents();

  const [isOpen, setIsOpen] = useState(true);
  const toggleOpen = (e: MouseEvent<HTMLElement>) => {
    e.preventDefault(); // Prevent the default toggle behavior
    setIsOpen(!isOpen);
  };

  const [selectedAssetId, setSelectedAssetId] = useAtom(selectedAssetIdAtom);
  const isViewOnlyMode = useAtomValue(isViewOnlyModeAtom);

  const setSelectedNodeType = useSetAtom(selectedNodeTypeAtom);

  const handleOnClick = useCallback(
    (
      e: MouseEvent<HTMLButtonElement> | KeyboardEvent<HTMLButtonElement>,
      id: string,
      assetType: ProjectAssetType,
    ) => {
      e.preventDefault();

      if (selectedAssetId === id) {
        setSelectedNodeType(null);
        setSelectedAssetId(null);
        return;
      }

      setSelectedNodeType(convertToSelectableNodeType(assetType));
      setSelectedAssetId(id);
    },
    [setSelectedNodeType, selectedAssetId, setSelectedAssetId],
  );

  if (!items.length) {
    return null;
  }

  return (
    <details open={isOpen} className={styles.wrapper}>
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <summary className={styles.summary} onClick={toggleOpen}>
        <Icon name={isOpen ? "arrowSmallDown" : "arrowSmallRight"} size={20} />
        <Text type="SmallPrint1">{`${assetType}s`.toUpperCase()}</Text>
      </summary>
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div onClick={(e) => e.stopPropagation()}>
        <ul className={styles.ul}>
          {items.map((item) => (
            <li key={item.zap_id}>
              <Link
                href={getAssetUrl(assetType, item)}
                target="_blank"
                className={styles.link}
                onClick={() =>
                  emitEventOnAssetOpen({
                    asset: "Zap",
                    location: "project panel",
                  })
                }
              >
                <div className={styles.row}>
                  <Icon
                    size={18}
                    color="BrandOrange"
                    name={getIconName(assetType)}
                  />
                  <span className={styles.truncate}>
                    {item.title || "Untitled Zap"}
                  </span>
                  <div className={styles.greenDotWrapper}>
                    {item.is_enabled && <div className={styles.greenDot} />}
                  </div>
                  {!isViewOnlyMode && (
                    <div className={styles.addButtonWrapper}>
                      <TooltipWrapper position="west" content="Add to Canvas">
                        {({ childProps }) => (
                          <button
                            aria-describedby={childProps["aria-describedby"]}
                            className={classNames(styles.buttonNoStyle, {
                              [styles.buttonSelected]:
                                selectedAssetId === item.zap_id,
                            })}
                            onClick={(e) =>
                              handleOnClick(e, item.zap_id, assetType)
                            }
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                handleOnClick(e, item.zap_id, assetType);
                              }
                            }}
                          >
                            <Icon
                              name="formAddCircle"
                              size={22}
                              color="GrayWarm9"
                            />
                          </button>
                        )}
                      </TooltipWrapper>
                    </div>
                  )}
                </div>
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </details>
  );
}
